/**
 * WorkStoppagesView
 *
 * Suivi des arrêts de travail, as a sub-view of "Leaves & Absences". Owns the suivi's
 * table (declared arrêts and the work-stoppage leaves still waiting for theirs), its pending-requests panel, the declare / details / delete dialogs, the deleted arrêts and the export.
 * Gated on the `workStoppages` scope by the caller — see permissions.md.
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGridApiRef } from "@mui/x-data-grid";
import moment from "moment";
import { Box, FormControlLabel, Switch } from "@mui/material";
import ImageButton from "../../components/buttons/ImageButton";
import exportIcon from "../../assets/images/export.png";
import exportIconFocus from "../../assets/images/exportFocus.png";
import addIcon from "../../assets/images/add.png";
import addIconFocus from "../../assets/images/addFocus.png";
import historyIcon from "../../assets/images/historyClockIcon.png";
import historyIconFocus from "../../assets/images/historyClockIconFocus.png";
import Langs, { Localize } from "../../app/lang/langs";
import ConfirmDialog from "../../components/popups/ConfirmDialog";
import { checkPermission, showSuccessToast, showErrorToast } from "../../app/utils/helpers";
import { config } from "../../app/utils/config";
import ExcelManager from "../../app/managers/excelManager";
import { setSelectedTeams } from "../../components/teams/teamsSlice";
import { getAllCollabs } from "../../components/collabs/collabsSlice";
import {
  getWorkStoppages,
  getWorkStoppageReference,
  deleteWorkStoppage,
  getPendingWorkStoppageLeaves,
  rejectWorkStoppageLeave,
} from "../../components/workStoppages/workStoppagesSlice";
import WorkStoppagePendingRequests from "../../components/workStoppages/WorkStoppagePendingRequests";
import { isExterne } from "../../components/workStoppages/WorkStoppageStatus";
import WorkStoppagesDataTable from "../collabs/WorkStoppagesDataTable";
import WorkStoppageFormModal from "../collabs/WorkStoppageFormModal";
import WorkStoppageDetailsModal from "../collabs/WorkStoppageDetailsModal";
import DeletedWorkStoppagesModal from "../collabs/DeletedWorkStoppagesModal";
import MobileWorkStoppagesList from "../collabs/mobile/MobileWorkStoppagesList";

/**
 * @param {Object} props
 * @param {React.ReactNode} [props.subTabs] - the phone's Congés / Arrêts switch, rendered
 *   inside this view's top bar (see LeavesAndAbsencesView).
 */
export default function WorkStoppagesView({ subTabs }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mounted = useRef(null);
  const dtWorkStoppagesApi = useGridApiRef();

  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const isMobile = useSelector((state) => state.settings.isMobile);
  const lang = useSelector((state) => state.settings.lang);
  const workStoppagesList = useSelector((state) => state.workStoppages.list);
  const pendingWorkStoppageLeaves = useSelector((state) => state.workStoppages.pendingLeaves);
  const allCollabsList = useSelector((state) => state.collabs.allCollabsList);

  // `workStoppageToEdit` doubles as the form's mode: a row means "correct this one", `null`
  // with the form open means "declare a new one".
  const [displayWorkStoppageForm, setDisplayWorkStoppageForm] = useState(false);
  const [workStoppageToEdit, setWorkStoppageToEdit] = useState(null);
  const [workStoppageDetails, setWorkStoppageDetails] = useState(null);
  const [workStoppageToDelete, setWorkStoppageToDelete] = useState(null);
  // A collab's pending request being validated: the form opens on that planning leave.
  const [workStoppagePreselectLeave, setWorkStoppagePreselectLeave] = useState(null);
  const [deletingWorkStoppage, setDeletingWorkStoppage] = useState(false);
  // "Arrêts supprimés": the deleted arrêts, read back from the audit trail.
  const [displayDeletedWorkStoppages, setDisplayDeletedWorkStoppages] = useState(false);
  // Server-side filter: "no CPAM reimbursement yet" has to be answered over every arrêt, not
  // over the rows already loaded, so flipping it re-queries.
  const [workStoppagesOnlyUnpaid, setWorkStoppagesOnlyUnpaid] = useState(false);
  // "Only my stoppages" — HR's own arrêts. Resolved server-side from the session.
  const [onlyMine, setOnlyMine] = useState(false);

  const hasWorkStoppagesEdit = checkPermission(config.scopes.workStoppages, config.permissionTypes.edit);

  useEffect(() => {
    if (!mounted.current) {
      // The référentiel is a settings table: fetched once, kept for the session.
      dispatch(getWorkStoppageReference());
      // The "new arrêt" collab picker reads the Collabs page's list, which is only loaded
      // there — fetched here when that page has not been opened this session.
      //
      // `workStoppages` counts as well as `collabs`: RH and Paie hold the former without
      // necessarily holding the latter, and gating on `collabs` alone left them with an
      // empty picker and no error to explain it (the request was simply never sent, and
      // nothing else in the session fills this list — the Collabs page that normally does
      // is itself behind `collabs:view`). The route accepts both scopes; see
      // planningRoutes.js for what that response carries.
      const canLoadCollabs =
        checkPermission(config.scopes.collabs, config.permissionTypes.view) ||
        checkPermission(config.scopes.workStoppages, config.permissionTypes.view);
      if (canLoadCollabs && !(allCollabsList || []).length)
        dispatch(getAllCollabs());
      mounted.current = true;
    }
  }, [dispatch]);

  // Re-runs when a server-side filter flips: both are answered by the API over every arrêt,
  // not over the loaded page.
  useEffect(() => {
    refreshWorkStoppages();
  }, [workStoppagesOnlyUnpaid, onlyMine, dispatch]);

  function refreshWorkStoppages() {
    dispatch(getWorkStoppages({
      ...(workStoppagesOnlyUnpaid ? { only_unpaid: 1 } : {}),
      ...(onlyMine ? { only_mine: 1 } : {}),
    }));
    // Validating or refusing takes edit, so only an editor is shown the requests.
    if (hasWorkStoppagesEdit) dispatch(getPendingWorkStoppageLeaves());
  }

  /**
   * The planning, weekly view, starting on the arrêt's first day — the URL form PlanningView
   * already reads (`WEEKLY` + DDMMYYYY). On the team the collab was in when the arrêt began,
   * which is where their leave sits; the current selection if that is unknown.
   */
  function openWorkStoppageInPlanning(row) {
    if (!row.start_date) return;
    const idTeam = row.id_team || selectedTeamsIds[0];
    if (row.id_team) dispatch(setSelectedTeams([row.id_team]));
    // `focus`: the planning scrolls to the arrêt's leave and flashes it.
    navigate(`/planning/${idTeam}/WEEKLY${moment(row.start_date).format("DDMMYYYY")}${row.id_planning ? `?focus=${row.id_planning}` : ""}`);
  }

  /** "Valider et déclarer": the declaration form, opened on that request's leave. */
  function validatePendingWorkStoppage(leave) {
    setWorkStoppageToEdit(null);
    setWorkStoppagePreselectLeave(leave.id);
    setDisplayWorkStoppageForm(true);
  }

  /** A table row with no suivi yet: the same form, opened on that row's planning leave. */
  function declareWorkStoppage(row) {
    validatePendingWorkStoppage({ id: row.id_planning });
  }

  async function rejectPendingWorkStoppage(leave) {
    const response = await dispatch(rejectWorkStoppageLeave({ id_planning: leave.id }));
    const data = response?.payload?.responseData;
    if (data && data.status === 1) {
      showSuccessToast(Localize("WORK_STOPPAGE_REJECTED", lang));
      dispatch(getPendingWorkStoppageLeaves());
    } else {
      showErrorToast(data?.message || data?.error || Localize("WORK_STOPPAGE_SAVE_FAILED", lang));
    }
  }

  async function confirmDeleteWorkStoppage() {
    // Guarded so a double click cannot send the delete twice.
    if (!workStoppageToDelete || deletingWorkStoppage) return;
    setDeletingWorkStoppage(true);
    try {
      const response = await dispatch(deleteWorkStoppage({ id: workStoppageToDelete.id }));
      const data = response?.payload?.responseData;
      if (data && data.status === 1) {
        showSuccessToast(Localize("WORK_STOPPAGE_DELETED", lang));
        refreshWorkStoppages();
      } else {
        showErrorToast(data?.message || Localize("WORK_STOPPAGE_SAVE_FAILED", lang));
      }
    } finally {
      setDeletingWorkStoppage(false);
      setWorkStoppageToDelete(null);
    }
  }

  function openNewWorkStoppage() {
    setWorkStoppageToEdit(null);
    setDisplayWorkStoppageForm(true);
  }

  // The besoin's field list, in its order (§3), pending the Power BI reporting (§2).
  // Reimbursements are summed rather than listed: one arrêt is one line here, and the
  // individual payments are on the arrêt's own screen. The API's /export endpoint writes
  // them to a second sheet if a per-payment extract is ever needed.
  function exportWorkStoppages() {
    const L = (key) => Localize(key, lang);
    const yesNo = (v) => (v === null || typeof v === "undefined" ? "" : Localize(v ? "YES" : "NO", lang));
    // An externe's arrêt is an absence only: its droits / CPAM / maintien cells stay blank.
    const wsExport = (workStoppagesList || []).map((ws) => {
      const externe = isExterne(ws);
      return {
        [L("type_collab")]: L(externe ? "WORK_STOPPAGE_EXTERNE" : "WORK_STOPPAGE_INTERNE"),
        [L("WORK_STOPPAGE_SUIVI")]: L(ws.has_suivi ? "WORK_STOPPAGE_SUIVI_DECLARED" : "WORK_STOPPAGE_SUIVI_NONE"),
        [L("ID")]: ws.id || "",
        [L("WORK_STOPPAGE_NUM_SS")]: ws.num_ss || "",
        [L("WORK_STOPPAGE_ENTITY")]: ws.entite || "",
        [L("WORK_STOPPAGE_SITE")]: ws.site || ws.team_name || "",
        [L("Name")]: ws.prenom || "",
        [L("Family Name")]: ws.nom || "",
        [L("WORK_STOPPAGE_CURRENT_TEAM")]: ws.current_team_name || "",
        [L("WORK_STOPPAGE_EMPLOYEE_STATUS")]: ws.statut || "",
        [L("WORK_STOPPAGE_SENIORITY_DATE")]: ws.date_anciennete ? moment(ws.date_anciennete).format("DD-MM-YYYY") : "",
        [L("WORK_STOPPAGE_EXIT_DATE")]: ws.date_sortie ? moment(ws.date_sortie).format("DD-MM-YYYY") : "",
        [L("WORK_STOPPAGE_MOTIF")]: ws.motif || "",
        [L("WORK_STOPPAGE_SUBROGATION")]: externe ? "" : yesNo(ws.subrogation),
        [L("WORK_STOPPAGE_SALARY_MAINTENANCE")]: externe ? "" : yesNo(ws.salary_maintenance),
        [L("WORK_STOPPAGE_RECEIVED_AT")]: ws.received_at ? moment(ws.received_at).format("DD-MM-YYYY") : "",
        [L("START_DATE")]: ws.start_date ? moment(ws.start_date).format("DD-MM-YYYY") : "",
        [L("END_DATE")]: ws.end_date ? moment(ws.end_date).format("DD-MM-YYYY") : "",
        [L("WORK_STOPPAGE_DECLARED_AT")]: ws.declared_at ? moment(ws.declared_at).format("DD-MM-YYYY") : "",
        // A leave with no suivi has none of the suivi's figures: blank, not "Initial" or 0 €.
        [L("WORK_STOPPAGE_STATUS")]: !ws.has_suivi ? "" : Localize(ws.stoppage_status === "PROLONGATION" ? "WORK_STOPPAGE_PROLONGATION" : "WORK_STOPPAGE_INITIAL", lang),
        [L("WORK_STOPPAGE_CARENCE_DAYS")]: externe ? "" : ws.carence_days ?? "",
        [L("WORK_STOPPAGE_CPAM_DAYS")]: externe ? "" : ws.cpam_days ?? "",
        [L("WORK_STOPPAGE_MANUAL")]: externe ? "" : yesNo(ws.cpam_days_overridden),
        [L("WORK_STOPPAGE_ABSENCE_DAYS")]: ws.absence_days ?? "",
        [L("WORK_STOPPAGE_CPAM_TOTAL")]: externe ? "" : ws.has_suivi ? Number(ws.cpam_total || 0) : "",
        [L("WORK_STOPPAGE_PREVOYANCE_TOTAL")]: externe ? "" : ws.has_suivi ? Number(ws.prevoyance_total || 0) : "",
        [L("WORK_STOPPAGE_CPAM_REIMBURSEMENT_STATUS")]: externe ? "" : ws.cpam_reimbursement_status ? L(`WORK_STOPPAGE_REIMBURSEMENT_${ws.cpam_reimbursement_status}`) : "",
        [L("WORK_STOPPAGE_PREVOYANCE_REIMBURSEMENT_STATUS")]: externe ? "" : ws.prevoyance_reimbursement_status ? L(`WORK_STOPPAGE_REIMBURSEMENT_${ws.prevoyance_reimbursement_status}`) : "",
        [L("WORK_STOPPAGE_THERAPEUTIC_RATE")]: externe ? "" : ws.therapeutic_rate ?? "",
        [L("WORK_STOPPAGE_MAINTENANCE_CATEGORY")]: externe ? "" : ws.maintenance_category || "",
        [L("WORK_STOPPAGE_MAINTENANCE_100")]: externe ? "" : ws.maintenance_days_100 ?? "",
        [L("WORK_STOPPAGE_MAINTENANCE_REDUCED")]: externe ? "" : ws.maintenance_days_80 ?? "",
        [L("WORK_STOPPAGE_MAINTENANCE_RATE")]: externe ? "" : ws.maintenance_partial_rate ? Number(ws.maintenance_partial_rate) : "",
        [L("WORK_STOPPAGE_MAINTENANCE_UNPAID")]: externe ? "" : ws.maintenance_days_unpaid ?? "",
        [L("Comments")]: ws.comments || "",
      };
    });
    ExcelManager.getInstance().generateAndDownloadExcelFromData(wsExport, "suivi_absences");
  }

  const declaredWorkStoppages = useMemo(
    () => (workStoppagesList || []).filter((ws) => ws.has_suivi),
    [workStoppagesList]
  );


  const pendingPanel = hasWorkStoppagesEdit ? (
    <WorkStoppagePendingRequests
      requests={pendingWorkStoppageLeaves}
      onValidate={validatePendingWorkStoppage}
      onReject={rejectPendingWorkStoppage}
      sx={{ mb: 1.5 }}
    />
  ) : null;

  return (
    <Box
      sx={isMobile === 2
        // The flex item inside LeavesAndAbsencesView's viewport-height column; see
        // MobileWorkStoppagesList for why the fixed bar and its pixel offset are gone.
        ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0, width: "100%" }
        : { height: "100%", width: "100%" }}
    >
      <Box
        // `.topMenu` is `position: fixed` on phones, which is what forced the offsets below.
        className={isMobile === 2 ? undefined : "topMenu"}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: isMobile === 2 ? 1 : 3 }}
      >
        <span className="headerTitle" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
          <Langs str="WORK_STOPPAGES" />
        </span>
        {/* The same orange image buttons as the other pages' top bars (CollabsTopMenu…). */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {hasWorkStoppagesEdit && (
            <ImageButton
              image={addIcon}
              imageFocus={addIconFocus}
              onClick={openNewWorkStoppage}
              active={true}
              tooltip={<Langs str="WORK_STOPPAGE_ADD" />}
              width={32}
            />
          )}
          <ImageButton
            image={historyIcon}
            imageFocus={historyIconFocus}
            onClick={() => setDisplayDeletedWorkStoppages(true)}
            active={true}
            tooltip="WORK_STOPPAGES_DELETED_TITLE"
            width={32}
          />
          <ImageButton
            image={exportIcon}
            imageFocus={exportIconFocus}
            onClick={exportWorkStoppages}
            active={true}
            tooltip="Export"
            width={32}
          />
        </Box>
      </Box>

      {isMobile === 2 && subTabs}

      {isMobile === 2 ? (
        <MobileWorkStoppagesList
          data={workStoppagesList}
          onViewDetails={(row) => setWorkStoppageDetails(row)}
          onDeclare={hasWorkStoppagesEdit ? declareWorkStoppage : undefined}
          header={(
            <>
              {/* The phone cards have no filter bar of their own, so the toggle sits above them. */}
              <FormControlLabel
                control={<Switch size="small" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />}
                label={<Langs str="WORK_STOPPAGE_ONLY_MINE" />}
                sx={{ mb: 1, whiteSpace: "nowrap" }}
              />
              {pendingPanel}
            </>
          )}
        />
      ) : (
        // Clears the fixed top bar once, for the pending panel and the table together — the
        // panel used to sit under the bar, and the table's card added its own 80px on top.
        <Box sx={{ pt: isMobile === 3 ? "95px" : "75px" }}>
          {pendingPanel && <Box sx={{ mx: "15px" }}>{pendingPanel}</Box>}
          <WorkStoppagesDataTable
            compact
            apiRef={dtWorkStoppagesApi}
            data={workStoppagesList}
            canEdit={hasWorkStoppagesEdit}
            onlyUnpaid={workStoppagesOnlyUnpaid}
            onOnlyUnpaidChange={setWorkStoppagesOnlyUnpaid}
            onlyMine={onlyMine}
            onOnlyMineChange={setOnlyMine}
            onViewDetails={(row) => setWorkStoppageDetails(row)}
            onOpenPlanning={openWorkStoppageInPlanning}
            onEdit={(row) => { setWorkStoppageToEdit(row); setDisplayWorkStoppageForm(true); }}
            onDelete={(row) => setWorkStoppageToDelete(row)}
            onDeclare={declareWorkStoppage}
          />
        </Box>
      )}

      {/* The same MUI dialogs serve desktop and mobile — they go full-screen on a phone
          rather than being duplicated as a mobile variant. */}
      <WorkStoppageFormModal
        visible={displayWorkStoppageForm}
        stoppage={workStoppageToEdit}
        // Only declared arrêts can be a prolongation's parent.
        list={declaredWorkStoppages}
        preselectedLeaveId={workStoppagePreselectLeave}
        onHide={() => { setDisplayWorkStoppageForm(false); setWorkStoppageToEdit(null); setWorkStoppagePreselectLeave(null); }}
        onSaved={refreshWorkStoppages}
      />
      <WorkStoppageDetailsModal
        visible={!!workStoppageDetails}
        stoppage={workStoppageDetails}
        canEdit={hasWorkStoppagesEdit}
        onHide={() => setWorkStoppageDetails(null)}
        onChanged={refreshWorkStoppages}
        // Edit from the view: the view closes and the form opens on the same arrêt.
        onEdit={(row) => { setWorkStoppageDetails(null); setWorkStoppageToEdit(row); setDisplayWorkStoppageForm(true); }}
      />
      <DeletedWorkStoppagesModal
        visible={displayDeletedWorkStoppages}
        onHide={() => setDisplayDeletedWorkStoppages(false)}
      />
      <ConfirmDialog
        open={!!workStoppageToDelete}
        danger
        loading={deletingWorkStoppage}
        title={<Langs str="WORK_STOPPAGE_DELETE_TITLE" />}
        message={<Langs str="WORK_STOPPAGE_DELETE_MSG" />}
        onConfirm={confirmDeleteWorkStoppage}
        onCancel={() => setWorkStoppageToDelete(null)}
      />
    </Box>
  );
}
